import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { Box, Divider, Paper, Rating, Stack, Typography, useTheme } from "@mui/material";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../components/InHouseTooltip/InHouseTooltip";
import { AnimatedNumber } from "../../../components/UIToolkit/Animations/AnimatedNumber";
import { PanelHeader } from "../../../components/UIToolkit/molecules/PanelHeader";
import { FONT_SIZE, ICON_SIZE, LETTER_SPACING } from "../../../theme/tokens";
import type { PbjStarThreshold } from "../../../utils/models";
import { brandBorder, brandWash, cardShadow, goldAccent, highBorder, highWash, PBJ_RADIUS, STAR_EMPTY } from "./colors";
import type { PlanOutcome } from "./planOutcome.utils";
import type { PeriodMode } from "./RecommendationsSidebar";
import { pointsChangeCopy } from "./signedCopy.utils";
import { ratingFromPoints } from "./stars.utils";

type Props = {
	// The lifted plan outcome (Total PPD Spend, Total Price, points gained, reaches-target),
	// computed once in ScoringAndRecommendations — never recomputed here.
	planOutcome: PlanOutcome;
	// Buffered target points the projected score is measured against (round for display only).
	targetPoints: number;
	// Star bands (BE lookup_stars tiers) — used ONLY to map the already-computed projected points
	// to the star rating the plan achieves (a display lookup, not a recompute of the outcome).
	starThresholds: PbjStarThreshold[];
	// Census + quarter/remaining-day counts drive the Total Price tooltip's math note.
	census: number;
	quarterDays: number;
	daysRemaining: number;
	// "quarter" = sustained-quarter tooltip; "qtd" = catch-up-window tooltip.
	periodMode: PeriodMode;
};

/**
 * The "Plan Summary" card — the bottom-RIGHT cell of the 2×2 prediction grid, beside the
 * Required Actions card. This is the CULMINATION of the flow (adjust levers → Required Actions →
 * THIS is the outcome), so it's the emphasized card — but a CALM one on the light working
 * surface, not a dark stage that competes with the hero. It's distinguished from its neighbors by
 * a brand-tinted header strip, and it carries the page's "final result" focal moment: the plan's
 * projected star rating + the large projected-score number over its target, then a prominent
 * verdict (tokenized success wash when the target is reached, brand wash when still short). Keeps
 * the vertical structure: the Total PPD Spend / Total Price (with the quarter/QTD tooltip) /
 * Points Gained rows, then the projected → target result block. Reads every figure from the
 * already-computed `planOutcome` + `targetPoints` — never recomputes (the projected star rating is
 * just a threshold lookup of the projected points). Returns null for an empty plan (mirrors
 * ActionItems), so the card only appears once the plan has an action.
 */
export const PlanSummaryCard: FC<Props> = ({ planOutcome, targetPoints, starThresholds, census, quarterDays, daysRemaining, periodMode }) => {
	const { t } = useTranslation();
	const theme = useTheme();

	if (!planOutcome.hasPlan) return null;

	const isTargetHit = planOutcome.reachesTarget;
	const projectedRounded = Math.round(planOutcome.projectedPoints);
	const targetRounded = Math.round(targetPoints);
	// The star rating the plan projects to — a pure display lookup of the already-computed
	// projected points against the BE star bands (mirrors the hero's current-stars headline).
	const projectedStars = ratingFromPoints(planOutcome.projectedPoints, starThresholds);
	const formatDollars = (value: number): string => value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	// A plan with any cut reads in cost / savings / net terms; an add-only plan keeps the familiar
	// Total PPD Spend + Total Price rows unchanged.
	const hasCuts = planOutcome.totalSavings > 0;
	const isNetSavings = planOutcome.totalPrice < 0;
	const isMixed = hasCuts && planOutcome.totalCost > 0;
	const savingsInk = theme.palette.success.main;
	// Signed points: a plan whose cuts give up more than its adds earn shows "Points change −N pts";
	// a plan with cuts that keeps every point shows "No points lost".
	// The label follows the ROUNDED figure's tone, so float residue never flips it to "Points change".
	const pointsCopy = pointsChangeCopy(planOutcome.pointsGained, hasCuts);
	const pointsLabelKey =
		pointsCopy.tone === "loss" || pointsCopy.tone === "kept" ? "pbj_model.action_plan.points_change" : "pbj_model.action_plan.points_gained";
	// Net PPD, read by its rounded sign: savings get their own label and ink; the figure is a magnitude.
	const isPpdSavings = Number(planOutcome.totalPpd.toFixed(2)) < 0;
	const pointsInk = pointsCopy.tone === "loss" ? theme.palette.error.main : pointsCopy.tone === "kept" ? savingsInk : theme.palette.primary.main;
	// Verdict ink on the light surface: success green when the target is reached, brand accent
	// when still short (both readable dark-on-light; gold stays reserved for the stars only).
	const verdictInk = isTargetHit ? theme.palette.success.main : theme.palette.primary.main;

	// One label↔value row — the muted label anchored left, the bold value right. `ink` colors the
	// value (points, savings).
	// DEFAULT: a plain figure (cost, price) carries no meaning in its color, so it takes body ink.
	const statRow = (label: ReactNode, value: ReactNode, opts?: { ink?: string; dataTestId?: string }): ReactNode => (
		<Stack direction="row" spacing={2} sx={{ justifyContent: "space-between", alignItems: "baseline" }}>
			<Typography variant="body2" sx={{ color: "text.secondary" }}>
				{label}
			</Typography>
			<Typography
				variant="body2"
				sx={{ fontWeight: 700, color: opts?.ink ?? "text.primary", fontVariantNumeric: "tabular-nums" }}
				data-testid={opts?.dataTestId}
			>
				{value}
			</Typography>
		</Stack>
	);

	// A dotted-underline label that explains itself on hover.
	const explainedLabel = (label: string, tooltip: string): ReactNode => (
		<InHouseTooltip arrow placement="top" title={tooltip}>
			<Box component="span" sx={{ cursor: "help", borderBottom: "1px dotted", borderColor: "text.disabled" }}>
				{label}
			</Box>
		</InHouseTooltip>
	);

	return (
		<Paper elevation={0} sx={{ borderRadius: PBJ_RADIUS.CARD, overflow: "hidden", boxShadow: cardShadow(theme) }} data-testid="pbj-model-plan-summary">
			{/* Brand-tinted header strip — the subtle accent that marks this as the culminating card
			    (the neighboring cards use a neutral `background.default` strip). */}
			<Box sx={{ p: 1.5, px: 2, bgcolor: brandWash(theme), borderBottom: `1px solid ${brandBorder(theme)}` }}>
				<PanelHeader icon={<SummarizeOutlinedIcon />} title={t("pbj_model.action_plan.summary")} />
			</Box>
			<Stack spacing={2} sx={{ p: { xs: 2, md: 2.5 } }}>
				{/* Cost + points stat rows, stacked vertically. */}
				{hasCuts ? (
					<>
						{/* Mixed or cut plan: what the adds cost, what the cuts save, then the NET as the
						    row that answers "what does this plan do to my budget". A swap's per-item points
						    can look contradictory (+ on the add, − on the cut); the net is what holds. */}
						{/* A savings-only plan has nothing to net, so its breakdown rows would just repeat
						    the net figure — only a mixed plan shows them. */}
						{isMixed && (
							<>
								{statRow(t("pbj_model.action_plan.total_cost"), t("pbj_model.recommendations.spend_format", { value: formatDollars(planOutcome.totalCost) }), {
									dataTestId: "pbj-model-action-total-cost",
								})}
								{statRow(
									t("pbj_model.action_plan.total_savings"),
									t("pbj_model.recommendations.spend_format", { value: formatDollars(planOutcome.totalSavings) }),
									{
										ink: savingsInk,
										dataTestId: "pbj-model-action-total-savings",
									},
								)}
							</>
						)}
						{/* The plan's net per-patient-day rate stays visible in cut mode — the hero quotes it
						    ("$5 PPD saved"), so the summary must too. Labelled by its sign. */}
						{statRow(
							t(isPpdSavings ? "pbj_model.action_plan.total_ppd_savings" : "pbj_model.action_plan.total_spend"),
							t("pbj_model.recommendations.spend_format", { value: Math.abs(planOutcome.totalPpd).toFixed(2) }),
							{ ink: isPpdSavings ? savingsInk : undefined, dataTestId: "pbj-model-action-total-ppd" },
						)}
						<Stack
							direction="row"
							spacing={2}
							sx={{ justifyContent: "space-between", alignItems: "baseline", pt: 1.25, borderTop: "1px solid", borderColor: "divider" }}
						>
							<Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary" }}>
								{explainedLabel(
									t(isNetSavings ? "pbj_model.action_plan.net_savings" : "pbj_model.action_plan.net_cost"),
									t("pbj_model.action_plan.net_tooltip"),
								)}
							</Typography>
							<Typography
								sx={{ fontSize: FONT_SIZE.TITLE_SM, fontWeight: 700, color: isNetSavings ? savingsInk : "text.primary", fontVariantNumeric: "tabular-nums" }}
								data-testid="pbj-model-action-net-price"
								data-net-direction={isNetSavings ? "savings" : "cost"}
							>
								{t("pbj_model.recommendations.spend_format", { value: formatDollars(Math.abs(planOutcome.totalPrice)) })}
							</Typography>
						</Stack>
					</>
				) : (
					<>
						{statRow(t("pbj_model.action_plan.total_spend"), t("pbj_model.recommendations.spend_format", { value: planOutcome.totalPpd.toFixed(2) }), {
							dataTestId: "pbj-model-action-total-ppd",
						})}
						{statRow(
							explainedLabel(
								t("pbj_model.action_plan.total_price"),
								periodMode === "qtd" && daysRemaining > 0
									? t("pbj_model.action_plan.total_price_tooltip_qtd", { census, daysRemaining })
									: t("pbj_model.action_plan.total_price_tooltip", { census, quarterDays }),
							),
							t("pbj_model.recommendations.spend_format", { value: formatDollars(planOutcome.totalPrice) }),
							{ dataTestId: "pbj-model-action-total-price" },
						)}
					</>
				)}
				{statRow(t(pointsLabelKey), t(pointsCopy.key, pointsCopy.values), { ink: pointsInk, dataTestId: "pbj-model-action-points-change" })}

				<Divider />

				{/* FOCAL RESULT — the plan's projected star rating + the "final result" score over its
				    target, then the verdict. A COMPACT callout (not a tall panel), washed by whether the
				    target is reached, so it reads as the outcome (success green when hit, brand when short). */}
				<Stack
					spacing={1}
					sx={{
						p: 1.5,
						borderRadius: PBJ_RADIUS.INSET,
						bgcolor: isTargetHit ? highWash(theme) : brandWash(theme),
						border: `1px solid ${isTargetHit ? highBorder(theme) : brandBorder(theme)}`,
					}}
				>
					<Box>
						<Typography
							sx={{ textTransform: "uppercase", letterSpacing: LETTER_SPACING.LABEL, fontWeight: 700, fontSize: FONT_SIZE.LABEL_XS, color: "text.secondary" }}
						>
							{t("pbj_model.gauge.projected_eyebrow", { count: projectedStars })}
						</Typography>
						<Rating
							value={projectedStars}
							readOnly
							sx={{
								mt: 0.25,
								fontSize: ICON_SIZE.INLINE,
								"& .MuiRating-iconFilled": { color: goldAccent(theme) },
								"& .MuiRating-iconEmpty": { color: STAR_EMPTY },
							}}
							data-testid="pbj-model-plan-summary-stars"
						/>
						<Stack direction="row" spacing={0.75} sx={{ alignItems: "baseline", mt: 0.25 }}>
							<Typography
								component="div"
								sx={{
									fontSize: FONT_SIZE.TITLE_XXL,
									fontWeight: 700,
									lineHeight: 1,
									letterSpacing: "-0.02em",
									color: "text.primary",
									fontVariantNumeric: "tabular-nums",
								}}
								data-testid="pbj-model-plan-summary-projected"
							>
								<AnimatedNumber value={projectedRounded} />
							</Typography>
							<Typography component="span" sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 500, color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
								{t("pbj_model.action_plan.of_target", { target: targetRounded })}
							</Typography>
						</Stack>
					</Box>

					{/* VERDICT — prominent, readable dark-on-light. Success ink + check when reached,
					    brand accent + trending-up when still short. */}
					<Stack
						direction="row"
						spacing={1}
						sx={{ alignItems: "center", pt: 1, borderTop: `1px solid ${isTargetHit ? highBorder(theme) : brandBorder(theme)}` }}
					>
						{isTargetHit ? (
							<>
								<CheckCircleRoundedIcon sx={{ fontSize: 20, color: verdictInk }} />
								<Typography sx={{ fontSize: FONT_SIZE.TITLE_SM, fontWeight: 700, color: verdictInk }} data-testid="pbj-model-action-target-hit">
									{t("pbj_model.action_plan.target_hit")}
								</Typography>
							</>
						) : (
							<>
								<TrendingUpIcon sx={{ fontSize: 20, color: verdictInk }} />
								<Typography sx={{ fontSize: FONT_SIZE.TITLE_SM, fontWeight: 700, color: verdictInk }} data-testid="pbj-model-action-need-more">
									{t("pbj_model.action_plan.need_more", { count: planOutcome.remainingPoints })}
								</Typography>
							</>
						)}
					</Stack>
				</Stack>
			</Stack>
		</Paper>
	);
};
